import React, { useState } from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import { useTheme } from "../../context/ThemeContext";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Card from "../../components/common/Card";

// Mesaj veri tipi tanımı
interface Message {
  id: number;
  platform: "email" | "whatsapp" | "facebook" | "instagram" | "twitter" | "linkedin";
  sender: string;
  subject: string;
  content: string;
  timestamp: string;
  status: "unread" | "read" | "replied";
  avatar?: string;
}

// Platform ikonları için yardımcı fonksiyon
const getPlatformIcon = (platform: string): string => {
  const icons: Record<string, string> = {
    email: "bi-envelope-fill",
    whatsapp: "bi-whatsapp",
    facebook: "bi-facebook",
    instagram: "bi-instagram",
    twitter: "bi-twitter",
    linkedin: "bi-linkedin",
  };
  return icons[platform] || "bi-chat-dots-fill";
};

// Platform renkleri için yardımcı fonksiyon
const getPlatformColor = (platform: string): string => {
  const colors: Record<string, string> = {
    email: "#ea4335",
    whatsapp: "#25d366",
    facebook: "#1877f2",
    instagram: "#e4405f",
    twitter: "#1da1f2",
    linkedin: "#0a66c2",
  };
  return colors[platform] || "#6c757d";
};

// Durum rozetleri için yardımcı fonksiyon
const getStatusBadgeClass = (status: string): string => {
  const classes: Record<string, string> = {
    unread: "bg-danger",
    read: "bg-secondary",
    replied: "bg-success",
  };
  return classes[status] || "bg-secondary";
};

const CommunicationsHubPage: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Örnek veri
  const dummyMessages: Message[] = [
    {
      id: 1,
      platform: "email",
      sender: "john.doe@example.com",
      subject: "Marketing Campaign Proposal",
      content: "Hi, I would like to discuss our upcoming marketing campaign. Can we schedule a call?",
      timestamp: "2024-03-20T10:30:00",
      status: "unread",
    },
    {
      id: 2,
      platform: "whatsapp",
      sender: "+1 234 567 8900",
      subject: "Product Inquiry",
      content: "Hello! I saw your product on Instagram. Can you tell me more about the pricing?",
      timestamp: "2024-03-20T09:15:00",
      status: "read",
    },
    {
      id: 3,
      platform: "facebook",
      sender: "Sarah Johnson",
      subject: "Customer Feedback",
      content: "Great service! Would love to see more content about your new features.",
      timestamp: "2024-03-19T15:45:00",
      status: "replied",
    },
    {
      id: 4,
      platform: "instagram",
      sender: "@tech_enthusiast",
      subject: "Collaboration Request",
      content: "Hey! Love your content. Would you be interested in a collaboration?",
      timestamp: "2024-03-19T14:20:00",
      status: "unread",
    },
    {
      id: 5,
      platform: "twitter",
      sender: "@marketing_pro",
      subject: "Industry Question",
      content: "What are your thoughts on the latest marketing trends?",
      timestamp: "2024-03-19T11:30:00",
      status: "read",
    },
    {
      id: 6,
      platform: "linkedin",
      sender: "Michael Brown",
      subject: "Business Partnership",
      content: "I think our companies could benefit from a strategic partnership.",
      timestamp: "2024-03-18T16:15:00",
      status: "unread",
    },
  ];

  // State tanımları
  const [messages, setMessages] = useState<Message[]>(dummyMessages);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [replyContent, setReplyContent] = useState("");

  // Filtreleme işlemi
  const filteredMessages = messages.filter((message) => {
    const matchesSearch =
      message.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.content.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPlatform = selectedPlatform === "all" || message.platform === selectedPlatform;
    const matchesStatus = selectedStatus === "all" || message.status === selectedStatus;

    return matchesSearch && matchesPlatform && matchesStatus;
  });

  // Yanıtlama işlemleri
  const handleReply = (message: Message) => {
    setSelectedMessage(message);
    setReplyDialogOpen(true);
  };

  const handleSendReply = () => {
    if (selectedMessage && replyContent.trim()) {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === selectedMessage.id ? { ...msg, status: "replied" } : msg
        )
      );
      setReplyDialogOpen(false);
      setReplyContent("");
      setSelectedMessage(null);
    }
  };

  const handleGenerateAIResponse = () => {
    // Yapay zeka yanıtı simülasyonu
    const aiResponse = `Thank you for your message regarding "${selectedMessage?.subject}". I'd be happy to help you with that. Let's schedule a call to discuss this further. Would you be available for a 30-minute meeting this week?`;
    setReplyContent(aiResponse);
  };

  // Filtreleri sıfırlama
  const resetFilters = () => {
    setSelectedPlatform("all");
    setSelectedStatus("all");
    setSearchTerm("");
  };

  return (
    <Container fluid className="marketing-dashboard communications-hub">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <i className="bi bi-chat-square-text-fill"></i>
            </div>
            <div className="header-text">
              <h1 className="page-title">Communications Hub</h1>
              <p className="page-subtitle">
                Manage all your communications across different platforms in one place
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtre Araç Çubuğu */}
      <div className="filter-toolbar">
        <Row className="g-3 align-items-center">
          <Col xs={12} md={4}>
            <Input
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<i className="bi bi-search"></i>}
            />
          </Col>
          <Col xs={12} md={3}>
            <select
              className="form-select"
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
            >
              <option value="all">All Platforms</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="twitter">Twitter</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </Col>
          <Col xs={12} md={3}>
            <select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
            </select>
          </Col>
          <Col xs={12} md={2}>
            <Button
              variant="outline"
              fullWidth
              onClick={resetFilters}
              leftIcon={<i className="bi bi-funnel"></i>}
              style={{ color: '#521DB3', borderColor: '#521DB3' }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </div>

      {/* Mesaj Listesi */}
      <div className="d-flex flex-column gap-3">
        {filteredMessages.length > 0 ? (
          filteredMessages.map((message) => (
            <div key={message.id} className="message-card">
              <div
                className="platform-indicator"
                style={{ background: getPlatformColor(message.platform) }}
              ></div>
              <div className="p-3">
                <Row className="align-items-center">
                  <Col xs={12} md={1} className="d-flex justify-content-center mb-3 mb-md-0">
                    <div
                      className="message-avatar"
                      style={{ background: getPlatformColor(message.platform) }}
                    >
                      <i className={`bi ${getPlatformIcon(message.platform)}`}></i>
                    </div>
                  </Col>
                  <Col xs={12} md={3} className="mb-2 mb-md-0">
                    <h6 className="message-sender">{message.sender}</h6>
                    <small className="message-time">
                      {new Date(message.timestamp).toLocaleString()}
                    </small>
                  </Col>
                  <Col xs={12} md={4} className="mb-2 mb-md-0">
                    <div className="message-subject">{message.subject}</div>
                    <p className="message-preview">
                      {message.content}
                    </p>
                  </Col>
                  <Col xs={12} md={2} className="mb-2 mb-md-0 text-center">
                    <span className={`badge ${getStatusBadgeClass(message.status)} text-capitalize`}>
                      {message.status}
                    </span>
                  </Col>
                  <Col xs={12} md={2}>
                    <Button
                      variant="primary"
                      size="sm"
                      fullWidth
                      onClick={() => handleReply(message)}
                      leftIcon={<i className="bi bi-reply"></i>}
                      style={{ color: '#fff' }}
                    >
                      Reply
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>
          ))
        ) : (
          <div className="alert alert-info">No messages found matching your filters.</div>
        )}
      </div>

      {/* Yanıt Modal */}
      <Modal
        show={replyDialogOpen}
        onHide={() => setReplyDialogOpen(false)}
        size="lg"
        centered
        contentClassName="bg-transparent border-0"
      >
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">
                Reply to {selectedMessage?.sender}
              </h5>
              <div className="text-muted small mt-1">
                {selectedMessage?.subject}
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              style={{ filter: isDark ? 'invert(1)' : 'none' }}
              onClick={() => setReplyDialogOpen(false)}
            ></button>
          </div>
          <div className="modal-body p-4">
            <div className="p-3 rounded-3 mb-3" style={{ backgroundColor: "var(--bg-secondary)" }}>
              <div className="text-muted small mb-2">Original Message:</div>
              <div>{selectedMessage?.content}</div>
            </div>
            <hr style={{ borderColor: "var(--border-color)" }} />
            <div className="mb-3">
              <label htmlFor="replyContent" className="form-label">Your Reply</label>
              <textarea
                id="replyContent"
                className="form-control"
                rows={4}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
              ></textarea>
            </div>
            <Button
              variant="outline"
              onClick={handleGenerateAIResponse}
              leftIcon={<i className="bi bi-robot"></i>}
              style={{ color: '#521DB3', borderColor: '#521DB3' }}
            >
              Generate Response with AI
            </Button>
          </div>
          <div className="modal-footer">
            <Button
              variant="ghost"
              onClick={() => setReplyDialogOpen(false)}
              style={{ color: 'var(--text-secondary)' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSendReply}
              disabled={!replyContent.trim()}
              leftIcon={<i className="bi bi-send"></i>}
              style={{ color: '#fff' }}
            >
              Send Reply
            </Button>
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default CommunicationsHubPage;
